import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle,
  CheckmarkIcon,
  EmptyCircle,
  LockIcon,
  PencilIcon,
  Plus,
  RightArrow,
  UploadIcon
} from '../../../icons/SvgIcons';
import {
  TextButton,
  PrimaryButton,
  WhiteButton
} from '../../ui/Button/Buttons';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { Pill } from '../../ui/Pill/Pill';
import { PillVariant } from '../../ui/Pill/Pill.types';
import { SideDrawer } from '../../ui/SideDrawer/SideDrawer';
import Modal from '../../ui/Modal/Modal';
import { FileUpload } from '../../ui/FileUpload/FileUpload';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  fetchRequirementStepsByAISystemId,
  fetchStepActionsByStepId,
  setFromRequirement
} from '../../../store/slices/requirementStepsSlice';
import { StepStatus } from '../../../types/RequirementSteps.types';
import { useNavigate } from 'react-router-dom';
import {
  supportedFiles,
  uploadFileLimits,
  radioButtonCardOptions,
  friaOptionsCardOptions,
  dpiaOptionsCardOptions
} from '../Documentation/Documentation.const';
import { useDocumentUpload } from '../../../hooks/useDocumentUpload';
import { resetFileState } from '../../../store/slices/fileUploadSlice';
import { Toast } from '../../ui/Toast/Toast';
import { RadioButtonCard } from '../../ui/RadioButton/RadioButtonCard';
import {
  setAnnexIVFieldsState,
  setCreateAnnexIVDraftResponse,
  setCreateDPIAResponse,
  setCreateEUDeclarationResponse,
  setCreateFRIAResponse,
  setDPIAField,
  setDPIAFieldsState,
  setEUDeclarationFieldsState,
  setEUDeclarationStatus,
  setFRIAFieldsState
} from '../../../store/slices/documentationSlice';
import {
  handleOpenSidePanel,
  setSidePanelContent,
  setSidePanelTitle
} from '../../../store/slices/sidePanelSlice';
import { createAnnexIVDraft } from '../../../services/AnnexIVService';
import {
  createEUDeclarationDraft,
  getEUDeclarationByAiSystem
} from '../../../services/EUDeclarationService';
import {
  mapAnnexContentToFields,
  mapDPIAContentToFields,
  mapEUDeclarationContentToFields,
  mapFRIAContentToFields
} from '../../../utils/documentationHelpers';
import {
  createFRIADraft,
  getFRIAByAiSystem
} from '../../../services/FRIAService';
import {
  createDPIADraft,
  getDPIAByAiSystem
} from '../../../services/DPIAService';
import { getDocumentation } from '../../../services/DocumentationService';
import { finishCompliance } from '../../../services/RequirementStepsService';
import { Steps } from '../../steps/Steps';
import { useAuthorization } from '../../../hooks/useAuthorization';

type RequirementListStepProps = {
  stepId: string;
  stepKey: string;
  order: number;
  title: string;
  status: StepStatus;
  overview: string;
  locked: boolean;
  onContinue?: () => void;
};

const getStatusConfig = (status: StepStatus) => {
  switch (status) {
    case 'COMPLETED':
      return {
        label: 'Completed',
        pillVariant: 'completed' as PillVariant,
        showButton: true,
        buttonText: 'View Details',
        numberClass: 'bg-purple-600 text-white'
      };
    case 'IN_PROGRESS':
      return {
        label: 'In Progress',
        pillVariant: 'in-progress' as PillVariant,
        showButton: true,
        buttonText: 'Continue Work',
        numberClass: 'border border-purple-600 bg-white text-purple-600'
      };
    case 'NOT_STARTED':
    default:
      return {
        label: 'Not Started',
        pillVariant: 'not-started' as PillVariant,
        showButton: true,
        buttonText: 'View Prerequisite',
        numberClass: 'border border-gray-200 text-gray-550'
      };
  }
};

const getBorderClasses = (status: StepStatus, locked: boolean) => {
  switch (status) {
    case 'COMPLETED':
      return 'border-gray-100 hover:border-purple-800';
    case 'IN_PROGRESS':
      return 'border-purple-500 hover:border-purple-800';
    case 'NOT_STARTED':
    default:
      return locked
        ? 'border-gray-100 bg-gray-25 hover:border-gray-200'
        : 'border-gray-100 hover:border-gray-300 bg-gray-25';
  }
};

export function RequirementListStep({
  stepId,
  stepKey,
  order,
  title,
  status,
  overview,
  locked,
  onContinue
}: RequirementListStepProps) {
  const dispatch = useAppDispatch();
  const { id: aiSystemId } = useParams<{ id: string }>();
  const { can, roleResolved } = useAuthorization();
  const [isHovered, setIsHovered] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showSupportingDocModal, setShowSupportingDocModal] = useState(false);
  const [showConformityAssessmentModal, setShowConformityAssessmentModal] =
    useState(false);
  const [showFRIAOptionsModal, setShowFRIAOptionsModal] = useState(false);
  const [showDPIAOptionsModal, setShowDPIAOptionsModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [finishingCompliance, setFinishingCompliance] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');
  const [selectedFRIAOption, setSelectedFRIAOption] = useState('');
  const [selectedDPIAOption, setSelectedDPIAOption] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [isFriaUploadFlow, setIsFriaUploadFlow] = useState(false);
  const [conformityRouteError, setConformityRouteError] = useState(false);
  const [isDpiaUploadFlow, setIsDpiaUploadFlow] = useState(false);

  const navigation = useNavigate();

  const { currentStepActions, actionsLoading, actionsError, steps } =
    useAppSelector((state) => state.requirementSteps);
  const {
    documentationReqBody,
    createAnnexIVDraftResponse,
    euDeclarationStatus
  } = useAppSelector((state) => state.documentation);
  const files = useAppSelector((state) => state.fileUpload);
  const isDisabled = locked && status === 'NOT_STARTED';
  const canManageRequirementList =
    roleResolved && can('aiSystems.requirementList.manage');
  const isStepActionDisabled = isDisabled || !canManageRequirementList;
  const showButtonOnHover = status === 'COMPLETED' || status === 'NOT_STARTED';
  const config = getStatusConfig(status);

  const buttonTitleMap: Record<string, string> = {
    'Generate the initial draft of Annex IV document':
      'Generate Annex IV Draft',
    'Finalize the Annex IV documentation': 'Finalize and sign Annex IV',
    'Generate draft of EU Declaration of Conformity': 'Generate EU DoC Draft',
    'Finalize and sign declaration': 'Finalize and sign EU DoC',
    'Acquired CE Mark': 'Finalize and sign EU DoC'
  };

  const buttonTitle = useMemo(() => {
    const currentStepAction = currentStepActions.find(
      (action) => action.completed === false
    );

    if (!currentStepAction) return '';
    return buttonTitleMap[currentStepAction.description];
  }, [currentStepActions]);

  useEffect(() => {
    if (stepKey === 'EU_DECLARATION_OF_CONFORMITY_AND_CE_MARK') {
      if (euDeclarationStatus === 'COMPLETED' && status !== 'COMPLETED') {
        return;
      }
      dispatch(setEUDeclarationStatus(status));
    }
  }, [dispatch, euDeclarationStatus, status, stepKey]);

  const getActionButton = (stepKey: string) => {
    const baseUrl = window.location.pathname.split('/requirement-list')[0];
    switch (stepKey) {
      case 'CONNECT_REPOSITORY':
        return {
          icon: Plus(),
          title: 'Connect to Repository',
          action: () => {
            navigation(`${baseUrl}/repository-management`);
            dispatch(setFromRequirement(true));
          }
        };
      case 'SUPPORTING_DOCUMENTATION':
        return {
          icon: UploadIcon('#111827', 16),
          title: 'Upload Documents',
          action: () => {
            setSelectedOption('');
            setIsFriaUploadFlow(false);
            setShowSupportingDocModal(true);
            setIsDrawerOpen(false);
          },
          actionComplete: () => {
            setShowCompleteModal(true);
            setIsDrawerOpen(false);
          }
        };
      case 'ANNEX_IV':
        return {
          icon: PencilIcon(),
          title: buttonTitle,
          action: async () => {
            await handleCreateAnnexIVDraft();
            setIsDrawerOpen(false);
          }
        };
      case 'CONFORMITY_ASSESSMENT':
        return {
          icon: UploadIcon('#111827', 16),
          title: 'Upload Documents',
          action: () => {
            setSelectedOption('');
            setShowConformityAssessmentModal(true);
            setIsDrawerOpen(false);
          }
        };
      case 'EU_DECLARATION_OF_CONFORMITY_AND_CE_MARK':
        return {
          icon: PencilIcon(),
          title: buttonTitle,
          action: async () => {
            await handleGenerateEUDeclaration();
            setIsDrawerOpen(false);
          }
        };
      case 'FRIA_REQUIRED':
        return {
          icon: Plus(),
          title: 'Provide FRIA Document',
          action: async () => {
            setIsDrawerOpen(false);
            await handleOpenExistingFRIADraftOrShowOptions();
          }
        };
      case 'DPIA_REQUIRED':
        return {
          icon: Plus(),
          title: 'Provide DPIA Document',
          action: async () => {
            setIsDrawerOpen(false);
            await handleOpenExistingDPIADraftOrShowOptions();
          }
        };

      default:
        return {
          icon: '',
          action: () => {}
        };
    }
  };
  const actionButton = getActionButton(stepKey);
  const showCompleteAction = actionButton.actionComplete && steps.length === 2;

  const getUploadCategory = () => {
    if (
      selectedOption === 'Internal Assessment' ||
      selectedOption === 'External Assessment'
    ) {
      return 'CONFORMITY_ASSESSMENT';
    }
    return 'OTHER';
  };

  const getConformityAssessmentType = ():
    | 'INTERNAL'
    | 'EXTERNAL'
    | undefined => {
    if (selectedOption === 'Internal Assessment') {
      return 'INTERNAL';
    }
    if (selectedOption === 'External Assessment') {
      return 'EXTERNAL';
    }
    return undefined;
  };

  const uploadLimits = isFriaUploadFlow
    ? uploadFileLimits.other
    : isDpiaUploadFlow
      ? uploadFileLimits.other
      : selectedOption.trim() === ''
        ? uploadFileLimits.default
        : selectedOption.trim() === 'Internal Assessment'
          ? uploadFileLimits.internal
          : uploadFileLimits.other;

  const { uploadFiles, uploading } = useDocumentUpload({
    aiSystemId: aiSystemId || '',
    pathPrefix: 'user-uploads',
    onSuccess: () => {
      setShowSupportingDocModal(false);
      setUploadedCount(files.length);
      setShowToast(true);
      dispatch(resetFileState());
      if (aiSystemId) {
        dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
      }
    },
    onError: (error) => {
      console.error('Upload error:', error);
    }
  });

  const handleCreateAnnexIVDraft = async () => {
    try {
      const response = await createAnnexIVDraft(
        aiSystemId || documentationReqBody.aiSystemId
      );
      dispatch(setCreateAnnexIVDraftResponse(response));
      dispatch(setAnnexIVFieldsState(mapAnnexContentToFields(response)));
      dispatch(handleOpenSidePanel(true));
      dispatch(setSidePanelTitle('Annex IV - Technical Documentation (Draft)'));
      dispatch(setSidePanelContent('ANNEX_IV'));
    } catch (error) {
      console.error('Error creating AnnexIV draft', error);
    }
  };

  const handleGenerateEUDeclaration = async () => {
    try {
      const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
      let response;

      try {
        response = await getEUDeclarationByAiSystem(aiSysId, {
          suppressErrorToast: true
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          response = await createEUDeclarationDraft(
            aiSysId,
            createAnnexIVDraftResponse?.id
          );
        } else {
          throw error;
        }
      }

      dispatch(setCreateEUDeclarationResponse(response));
      dispatch(
        setEUDeclarationFieldsState(mapEUDeclarationContentToFields(response))
      );
      dispatch(setSidePanelTitle('EU Declaration of Conformity (Draft)'));
      dispatch(setSidePanelContent('EU_DECLARATION'));
      dispatch(handleOpenSidePanel(true));
    } catch (error: any) {
      console.error('Error creating EU Declaration draft', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleGenerateFRIA = async () => {
    try {
      const aiSysId = aiSystemId || documentationReqBody.aiSystemId;

      // Try to get existing FRIA draft first
      let response: Awaited<ReturnType<typeof getFRIAByAiSystem>> | null = null;
      try {
        response = await getFRIAByAiSystem(aiSysId, {
          suppressErrorToast: true
        });
        console.log('Found existing FRIA draft:', response);
      } catch (error: any) {
        // If not found (404), create a new draft
        if (error.response?.status === 404) {
          console.log('No existing FRIA found, creating new draft');
          response = await createFRIADraft(aiSysId);
        } else {
          throw error;
        }
      }

      if (response) {
        dispatch(setCreateFRIAResponse(response));
        dispatch(setFRIAFieldsState(mapFRIAContentToFields(response)));
        dispatch(setSidePanelTitle('FRIA Document (Draft)'));
        dispatch(setSidePanelContent('FRIA'));
        dispatch(handleOpenSidePanel(true));
      }
    } catch (error: any) {
      console.error('Error creating FRIA draft', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleOpenExistingFRIADraftOrShowOptions = async () => {
    const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
    if (!aiSysId) {
      setSelectedFRIAOption('');
      setShowFRIAOptionsModal(true);
      return;
    }

    try {
      const response = await getFRIAByAiSystem(aiSysId, {
        suppressErrorToast: true
      });

      dispatch(setCreateFRIAResponse(response));
      dispatch(setFRIAFieldsState(mapFRIAContentToFields(response)));
      dispatch(setSidePanelTitle('FRIA Document (Draft)'));
      dispatch(setSidePanelContent('FRIA'));
      dispatch(handleOpenSidePanel(true));
      return;
    } catch (error: any) {
      // If draft does not exist (404), user should choose Generate or Upload.
      if (error?.response?.status !== 404) {
        console.error('Error loading existing FRIA draft', error);
      }
    }

    setSelectedFRIAOption('');
    setShowFRIAOptionsModal(true);
  };

  const handleFriaOptionsNextClick = async () => {
    setShowFRIAOptionsModal(false);

    if (selectedFRIAOption === 'Generate FRIA') {
      await handleGenerateFRIA();
    } else if (selectedFRIAOption === 'Upload FRIA') {
      setSelectedOption('');
      setIsFriaUploadFlow(true);
      setShowSupportingDocModal(true);
    }

    setSelectedFRIAOption('');
  };

  const handleDpiaOptionsNextClick = async () => {
    setShowDPIAOptionsModal(false);

    if (selectedDPIAOption === 'Generate DPIA') {
      await handleGenerateDPIA();
    } else if (selectedDPIAOption === 'Upload DPIA') {
      setSelectedOption('');
      setIsDpiaUploadFlow(true);
      setShowSupportingDocModal(true);
    }

    setSelectedDPIAOption('');
  };

  const handleOpenExistingDPIADraftOrShowOptions = async () => {
    const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
    if (!aiSysId) {
      setSelectedDPIAOption('');
      setShowDPIAOptionsModal(true);
      return;
    }

    try {
      const response = await getDPIAByAiSystem(aiSysId, {
        suppressErrorToast: true
      });

      dispatch(setCreateDPIAResponse(response));
      dispatch(setDPIAFieldsState(mapDPIAContentToFields(response)));
      dispatch(setSidePanelTitle('DPIA Document (Draft)'));
      dispatch(setSidePanelContent('DPIA'));
      dispatch(handleOpenSidePanel(true));
      return;
    } catch (error: any) {
      // If draft does not exist (404), user should choose Generate or Upload.
      if (error?.response?.status !== 404) {
        console.error('Error loading existing DPIA draft', error);
      }
    }

    setSelectedDPIAOption('');
    setShowDPIAOptionsModal(true);
  };

  const handleGenerateDPIA = async () => {
    try {
      const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
      let response;

      try {
        // Try to get existing DPIA draft first
        response = await getDPIAByAiSystem(aiSysId, {
          suppressErrorToast: true
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          console.error('No existing DPIA found for AI System ID:', aiSysId);
          // If not found (404), create a new draft
          response = await createDPIADraft(aiSysId);
        } else {
          throw error;
        }
      }

      dispatch(setCreateDPIAResponse(response));
      dispatch(setDPIAFieldsState(mapDPIAContentToFields(response)));
      dispatch(setSidePanelTitle('DPIA Document (Draft)'));
      dispatch(setSidePanelContent('DPIA'));
      dispatch(handleOpenSidePanel(true));
    } catch (error) {
      console.error('Error creating DPIA draft', error);
    }
  };

  const handleCompleteButton = async () => {
    if (!aiSystemId) return;

    setFinishingCompliance(true);
    try {
      await finishCompliance(aiSystemId);
      setShowCompleteModal(false);
      dispatch(fetchRequirementStepsByAISystemId(aiSystemId));
    } catch (error) {
      console.error('Error finishing compliance:', error);
    } finally {
      setFinishingCompliance(false);
    }
  };

  const hasInternalAssessmentDocument = async (
    aiSystemId: string
  ): Promise<boolean> => {
    let offset = 0;
    let total = 0;

    do {
      const response = await getDocumentation({
        ...documentationReqBody,
        aiSystemId,
        offset
      });

      const foundDocument = response.items.some(
        (doc) =>
          doc.category === 'CONFORMITY_ASSESSMENT' &&
          doc.conformityAssessmentType === 'INTERNAL'
      );

      if (foundDocument) return true;

      total = response.total;
      offset += response.items.length;
    } while (offset < total);

    return false;
  };

  const handleConformityNextClick = async () => {
    setConformityRouteError(false);

    const aiSysId = aiSystemId || documentationReqBody.aiSystemId;
    if (!aiSysId) return;

    if (selectedOption === 'External Assessment') {
      const hasInternal = await hasInternalAssessmentDocument(aiSysId);

      if (hasInternal) {
        setConformityRouteError(true);
        return;
      }
    }

    setIsFriaUploadFlow(false);
    setShowSupportingDocModal(true);
    setShowConformityAssessmentModal(false);
  };

  const handleButtonClick = async () => {
    if (!canManageRequirementList) {
      return;
    }

    setIsDrawerOpen(true);
    dispatch(fetchStepActionsByStepId(stepId));

    if (onContinue) {
      onContinue();
    }
  };

  const getCircleStyle = (actionComplete: boolean) => {
    if (actionComplete) {
      return <div className="min-w-[22px]">{CheckCircle('#717AE3', 22)}</div>;
    } else {
      return <div className="min-w-[22px]">{EmptyCircle('#767681', 16)}</div>;
    }
  };

  return (
    <div className="flex gap-4 items-stretch group">
      {/* Number Badge - Outside the card */}
      <div className="flex flex-col items-center gap-2 relative">
        {status === 'COMPLETED' ? (
          <div className="w-6 h-6 flex items-center justify-center bg-purple-600 rounded-full">
            {CheckmarkIcon()}
          </div>
        ) : (
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${config.numberClass}`}
          >
            {order}
          </div>
        )}
        {/* Gray Line */}
        <div className="w-[2px] flex-1 bg-gray-250v2"></div>
      </div>

      {/* Card Content */}
      <div
        className={`relative flex-1 flex flex-col gap-2 p-4 ${
          status === 'IN_PROGRESS' ? '' : 'pb-[11px]'
        } border ${getBorderClasses(status, locked)} rounded-lg transition-all`}
      >
        {/* Header with Title and Status */}
        <div className="flex items-start justify-between gap-4">
          <h3
            className={`text-base flex gap-1 font-semibold ${
              locked ? 'text-gray-800' : 'text-gray-900'
            }`}
          >
            {title}
            {/* Lock indicator for locked steps */}
            {isDisabled && (
              <Tooltip
                content="Complete previous steps to unlock"
                position="bottom"
                showArrow={false}
                delay={100}
              >
                <div
                  className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-900v2"
                  onMouseEnter={() => {
                    setIsHovered(true);
                  }}
                  onMouseLeave={() => {
                    setIsHovered(false);
                  }}
                >
                  {LockIcon(isHovered ? 'white' : '#4E4E56')}
                </div>
              </Tooltip>
            )}
          </h3>
          <Pill variant={config.pillVariant}>{config.label}</Pill>
        </div>

        {/* Content Container */}
        <div className="flex gap-2 justify-between">
          {/* Description */}
          <p
            className={`text-sm font-normal text-left ${
              locked ? 'text-gray-550' : 'text-gray-800'
            }`}
          >
            {overview}
          </p>

          {/* Action Button */}
          {config.showButton && (
            <div
              className={`flex justify-end mt-auto min-w-[150px] h-10 ${
                showButtonOnHover
                  ? 'opacity-0 group-hover:opacity-100 transition-opacity'
                  : ''
              }`}
            >
              <TextButton
                onClick={handleButtonClick}
                disabled={isStepActionDisabled}
                className="text-purple-800 break-keep"
                icon={RightArrow()}
                iconPosition="right"
                size="xs"
              >
                {config.buttonText}
              </TextButton>
            </div>
          )}
        </div>
      </div>

      {/* Side Drawer */}
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={`Step ${order}: ${title}`}
        status={<Pill variant={config.pillVariant}>{config.label}</Pill>}
        showActionButton={
          (config.label === 'In Progress' || config.label === 'Not Started') &&
          !locked &&
          canManageRequirementList
        }
        locked={locked}
        overview={
          <p className="text-sm text-gray-900v2 leading-[20px]">{overview}</p>
        }
        requiredActions={
          actionsLoading ? (
            <div className="text-sm text-gray-500">Loading actions...</div>
          ) : actionsError ? (
            <div className="text-sm text-red-600">Error: {actionsError}</div>
          ) : currentStepActions.length > 0 ? (
            <ul className="space-y-3">
              {currentStepActions.map((action) => (
                <li
                  key={action.id}
                  className="flex items-start justify-start gap-1 text-gray-550"
                >
                  {actionButton.title === 'Upload Documents'
                    ? '•'
                    : getCircleStyle(action.completed)}
                  <span className={`text-sm`}>{action.description}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-gray-500">No actions available</div>
          )
        }
        actionButton={
          !actionsLoading && (
            <div
              className={`flex ${showCompleteAction ? 'justify-between' : 'justify-end'}`}
            >
              {showCompleteAction && (
                <PrimaryButton
                  onClick={actionButton.actionComplete}
                  disabled={!canManageRequirementList}
                  size="sm"
                >
                  Complete
                </PrimaryButton>
              )}
              <PrimaryButton
                icon={actionButton.icon}
                onClick={actionButton.action}
                disabled={!canManageRequirementList}
                size="sm"
              >
                {actionButton.title}
              </PrimaryButton>
            </div>
          )
        }
      />

      <Modal
        open={showSupportingDocModal}
        setOpen={setShowSupportingDocModal}
        dialogPanelClassnames="w-[820px] overflow-visible"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            {isFriaUploadFlow
              ? 'FRIA Document'
              : isDpiaUploadFlow
                ? 'DPIA Document'
                : 'Upload Documents'}
          </p>
          <FileUpload
            suportedFiles={supportedFiles}
            maxFiles={uploadLimits.max}
            minFiles={uploadLimits.min}
            uploading={uploading}
            handleCancel={() => {
              setShowSupportingDocModal(false);
              setSelectedOption('');
              setIsFriaUploadFlow(false);
              setIsDpiaUploadFlow(false);
              dispatch(resetFileState());
            }}
            handleUpload={
              isFriaUploadFlow
                ? () => uploadFiles(files, 'FRIA')
                : isDpiaUploadFlow
                  ? () => uploadFiles(files, 'DPIA')
                  : () =>
                      uploadFiles(
                        files,
                        getUploadCategory(),
                        getConformityAssessmentType()
                      )
            }
          />
        </div>
      </Modal>

      <Modal
        open={showConformityAssessmentModal}
        setOpen={setShowConformityAssessmentModal}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="relative flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            Conformity Assessment
          </p>
          <div className="flex gap-6">
            {radioButtonCardOptions.map((option) => (
              <RadioButtonCard
                key={option.label}
                label={option.label}
                desctiption={option.description}
                documentRequirement={option.documentRequirement}
                selected={selectedOption === option.label}
                onChange={() => {
                  setSelectedOption(option.label);
                  setConformityRouteError(false);
                }}
              />
            ))}
          </div>
          {conformityRouteError && (
            <p className="absolute bottom-12 text-sm text-red-600">
              Delete all Internal Assessment documents in Documentation before
              switching to External Assessment.
            </p>
          )}
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => setShowConformityAssessmentModal(false)}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleConformityNextClick}
              disabled={!selectedOption || conformityRouteError}
              className="w-[82px]"
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showFRIAOptionsModal}
        setOpen={setShowFRIAOptionsModal}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            FRIA Document
          </p>
          <div className="flex gap-6">
            {friaOptionsCardOptions.map((option) => (
              <RadioButtonCard
                key={option.label}
                label={option.label}
                desctiption={option.description}
                documentRequirement={option.documentRequirement}
                selected={selectedFRIAOption === option.label}
                onChange={() => setSelectedFRIAOption(option.label)}
              />
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => {
                setShowFRIAOptionsModal(false);
                setSelectedFRIAOption('');
              }}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleFriaOptionsNextClick}
              disabled={!selectedFRIAOption}
              className="w-[82px]"
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showDPIAOptionsModal}
        setOpen={setShowDPIAOptionsModal}
        dialogPanelClassnames="w-[820px]"
      >
        <div className="flex flex-col gap-6">
          <p className="text-gray-900 text-lg leading-6 font-medium">
            DPIA Document
          </p>
          <div className="flex gap-6">
            {dpiaOptionsCardOptions.map((option) => (
              <RadioButtonCard
                key={option.label}
                label={option.label}
                desctiption={option.description}
                documentRequirement={option.documentRequirement}
                selected={selectedDPIAOption === option.label}
                onChange={() => setSelectedDPIAOption(option.label)}
              />
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => {
                setShowDPIAOptionsModal(false);
                setSelectedDPIAOption('');
              }}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleDpiaOptionsNextClick}
              disabled={!selectedDPIAOption}
              className="w-[82px]"
            >
              Next
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={showCompleteModal}
        setOpen={setShowCompleteModal}
        dialogPanelClassnames="w-[475px]"
      >
        <>
          <div className="flex flex-col gap-2">
            <p className="text-gray-900 text-lg leading-6 font-medium">
              Do you want to complete?
            </p>

            <p className="text-sm text-gray-500">
              Once you click Complete, this step will be permanently locked.
              <br />
              No further uploads or changes will be allowed.
            </p>

            <p className="text-sm text-gray-500">
              The workflow will be marked as Completed, and the AI system status
              will update to Compliant.
            </p>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <WhiteButton
              onClick={() => {
                setShowCompleteModal(false);
              }}
            >
              Cancel
            </WhiteButton>
            <PrimaryButton
              onClick={handleCompleteButton}
              disabled={finishingCompliance}
            >
              {finishingCompliance ? 'Completing...' : 'Complete'}
            </PrimaryButton>
          </div>
        </>
      </Modal>

      {showToast && (
        <Toast
          setShowToast={setShowToast}
          showToast={showToast}
          text={
            uploadedCount > 1 ? 'Documents Uploaded!' : 'Document Uploaded!'
          }
        />
      )}
    </div>
  );
}
